import React, { useEffect, useState } from "react";
import { FiDownload, FiEdit, FiTrash2 } from "react-icons/fi";
import { DownloadInterface } from "../../common/interfaces";
import { extractErrorMessage } from "../../common/utils/axios.isAxiosError";
import ConfirmDialog from "../../components/ConfirmDialog";
import CreateButton from "../../components/CreateButton";
import CustomTooltip from "../../components/CustomTooltip";
import InfoBanner from "../../components/InfoBanner";
import DownloadCreateModal from "../../components/modal/DownloadCreateModal";
import StatusSwitch from "../../components/switch/StatusSwitch";
import { ToastError, ToastSuccess } from "../../components/toaster/toast.utils";
import { useAuth } from "../../hooks/useAuth";
import api from "../../services/api";
import {
  ActionButton,
  ActionsCell,
  Container,
  Table,
  TableContainer,
  Title
} from "../sharedTable.styled";

const formatSize = (bytes: number) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const Downloads: React.FC = () => {
  const { isAdmin } = useAuth();
  const [downloads, setDownloads] = useState<DownloadInterface[]>([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [selected, setSelected] = useState<DownloadInterface | null>(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [toDelete, setToDelete] = useState<DownloadInterface | null>(null);

  const fetchDownloads = async () => {
    try {
      // Sempre traz tudo (ativo e inativo): a lista aparece pra todo mundo,
      // só o botão de baixar some quando o admin inativa — ver Ações abaixo.
      const { data } = await api.get("/downloads", {
        params: { showActives: "false" }
      });
      setDownloads(data ?? []);
    } catch (error) {
      ToastError(extractErrorMessage(error));
    }
  };

  useEffect(() => {
    fetchDownloads();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleOpenCreate = () => {
    setSelected(null);
    setModalOpen(true);
  };

  const handleOpenEdit = (download: DownloadInterface) => {
    setSelected(download);
    setModalOpen(true);
  };

  const handleToggle = async (download: DownloadInterface) => {
    try {
      await api.patch(`/downloads/${download.download_id}/toggle-active`);
      ToastSuccess(download.is_active ? "Download inativado." : "Download ativado.");
      fetchDownloads();
    } catch (error) {
      ToastError(extractErrorMessage(error));
    }
  };

  const handleOpenDelete = (download: DownloadInterface) => {
    setToDelete(download);
    setDeleteOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!toDelete) return;
    try {
      await api.delete(`/downloads/${toDelete.download_id}`);
      ToastSuccess("Download excluído.");
      fetchDownloads();
    } catch (error) {
      ToastError(extractErrorMessage(error));
    } finally {
      setDeleteOpen(false);
      setToDelete(null);
    }
  };

  return (
    <Container>
      <Title>
        Downloads
        {isAdmin && (
          <CreateButton onClick={handleOpenCreate} style={{ marginLeft: "14px" }}>
            <FiDownload size={18} /> Novo Download
          </CreateButton>
        )}
      </Title>

      <InfoBanner icon={<FiDownload size={26} />} title="Downloads">
        Arquivos disponibilizados pela equipe — como o instalador do app
        mobile. Clique em baixar para salvar o arquivo mais recente.
      </InfoBanner>

      <TableContainer>
        <Table>
          <thead>
            <tr>
              <th>Nome</th>
              <th>Descrição</th>
              <th>Tamanho</th>
              <th>Atualizado em</th>
              {isAdmin && <th>Status</th>}
              <th>Ações</th>
            </tr>
          </thead>
          <tbody>
            {downloads.map((item) => (
              <tr key={item.download_id}>
                <td>{item.name}</td>
                <td>{item.description || "-"}</td>
                <td>{formatSize(item.size_bytes)}</td>
                <td>{new Date(item.updated_at).toLocaleDateString("pt-BR")}</td>
                {isAdmin && (
                  <td>
                    <StatusSwitch
                      checked={item.is_active === 1}
                      onToggle={() => handleToggle(item)}
                    />
                  </td>
                )}
                <ActionsCell>
                  {item.is_active === 1 ? (
                    <CustomTooltip title="Baixar">
                      <a
                        href={item.url ?? undefined}
                        target="_blank"
                        rel="noreferrer"
                        style={{ display: "inline-flex", color: "inherit" }}
                      >
                        <FiDownload size={20} color="#2b7bff" />
                      </a>
                    </CustomTooltip>
                  ) : (
                    <CustomTooltip title="Indisponível no momento">
                      <span style={{ display: "inline-flex" }}>
                        <FiDownload size={20} color="var(--text-dim)" />
                      </span>
                    </CustomTooltip>
                  )}
                  {isAdmin && (
                    <>
                      <CustomTooltip title="Editar">
                        <ActionButton onClick={() => handleOpenEdit(item)}>
                          <FiEdit size={18} />
                        </ActionButton>
                      </CustomTooltip>
                      <CustomTooltip title="Excluir">
                        <ActionButton onClick={() => handleOpenDelete(item)}>
                          <FiTrash2 size={18} color="#fb7185" />
                        </ActionButton>
                      </CustomTooltip>
                    </>
                  )}
                </ActionsCell>
              </tr>
            ))}
            {downloads.length === 0 && (
              <tr>
                <td
                  colSpan={isAdmin ? 6 : 5}
                  style={{ textAlign: "center", color: "var(--text-dim)" }}
                >
                  Nenhum download cadastrado.
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </TableContainer>

      <DownloadCreateModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onSaved={fetchDownloads}
        download={selected}
      />

      <ConfirmDialog
        open={deleteOpen}
        title="Excluir download"
        description={`Excluir "${toDelete?.name ?? ""}" definitivamente? O arquivo também será removido do armazenamento. Essa ação não pode ser desfeita.`}
        confirmText="Excluir"
        onClose={() => setDeleteOpen(false)}
        onConfirm={handleConfirmDelete}
      />
    </Container>
  );
};

export default Downloads;
